import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@bowfast/ui/components/tooltip";
import { cn } from "@bowfast/ui/lib/utils";
import { motion } from "framer-motion";
import { CheckCircle2Icon, ClockIcon, CreditCardIcon, MapPinIcon, PlayCircleIcon, UserPlusIcon } from "lucide-react";
import type { ExpeditionStatus } from "~/graphql/generated";
import { fadeUp, stagger } from "~/lib/animations";
import { KIND_LABEL, STATE_ADVANCE } from "~/lib/expedition-constants";
import { fmtDate, fmtDays, fmtMiles, fmtPrice } from "~/lib/fmt";
import { EXPENSE_CATEGORIES } from "~/pages/expeditions/expedition-detail.mock";
import type { ExpeditionDetailData, ExpeditionExpense } from "~/pages/expeditions/expeditions.types";

function DetailRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}

// StateAdvancePanel driven by STATE_ADVANCE config from expedition-constants.
// TODO(backend/stateAdvance): wire the primary button to UpdateExpeditionStatus mutation.
// Pass `onAdvance: (nextStatus: ExpeditionStatus) => void` prop when mutation is ready.
function StateAdvancePanel({ status }: Readonly<{ status: ExpeditionStatus }>) {
  const advance = STATE_ADVANCE[status];
  if (!advance) return null; // COMPLETED has no advance step

  const isActive = status === "ACTIVE";
  const isCancelled = status === "CANCELLED";

  return (
    <Card
      className={cn(
        isActive && "border-shallow-200 bg-shallow-50/50 dark:border-shallow-800 dark:bg-shallow-950/30",
        isCancelled && "border-coral-200 bg-coral-50/50 dark:border-coral-800 dark:bg-coral-950/30",
        !isActive && !isCancelled && "border-abyss-200 bg-abyss-50/50 dark:border-abyss-800 dark:bg-abyss-950/30",
      )}
    >
      <CardContent className="space-y-3 py-5">
        <p
          className={cn(
            "text-[0.625rem] font-bold uppercase tracking-[0.07em]",
            isActive && "text-shallow-600",
            isCancelled && "text-coral-600",
            !isActive && !isCancelled && "text-abyss-600",
          )}
        >
          {advance.label}
        </p>
        <p className="font-heading text-sm font-semibold">{advance.title}</p>
        <p className="text-xs text-muted-foreground">{advance.body}</p>
        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            className={cn(
              "gap-2",
              isActive && "bg-shallow-500 text-white hover:bg-shallow-600",
              isCancelled &&
                "border-coral-300 bg-transparent text-coral-600 hover:bg-coral-50 dark:border-coral-700 dark:text-coral-400 dark:hover:bg-coral-950",
            )}
            variant={isCancelled ? "outline" : "default"}
          >
            <PlayCircleIcon className="size-3.5" />
            {advance.action}
          </Button>
          {/* Active expedition also shows Cancel voyage action */}
          {isActive && (
            <Button
              size="sm"
              variant="outline"
              className="gap-2 border-coral-300 text-coral-600 hover:bg-coral-50 dark:border-coral-700 dark:text-coral-400 dark:hover:bg-coral-950"
            >
              Cancel voyage
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function OverviewTab({
  expedition,
  expenses,
}: Readonly<{
  expedition: ExpeditionDetailData;
  expenses: ExpeditionExpense[];
}>) {
  const confirmedCrewCount = expedition.crew.filter((m) => m.status === "CONFIRMED").length;
  const open = expedition.maxCrew - confirmedCrewCount;
  const currency = expenses[0]?.currency ?? "EUR";
  const totalExpenses = expenses.reduce((s, e) => s + e.amountCents / 100, 0);
  // TODO(backend/expense): add `category` field to Expense type so the stacked breakdown bar
  // can group expenses by category (marina, fuel, provisions, etc.). Until then, show a single bar.
  const expensesByCategory = EXPENSE_CATEGORIES.map((cat) => ({
    ...cat,
    amount: 0, // placeholder — will use cat.key vs expense.category once backend adds the field
  })).filter((c) => c.amount > 0);

  // Derived display values
  const homePort = expedition.vessels[0]?.vessel.homePort ?? "—";
  const departsLabel = fmtDate(expedition.departureDate);
  const arrivesLabel = fmtDate(expedition.returnDate);
  const duration = fmtDays(expedition.departureDate, expedition.returnDate);
  const distanceNm = expedition.route?.totalDistanceNm ?? 0;
  const primaryVesselName = expedition.vessels[0]?.vessel.name ?? "—";

  const showStatePanel =
    expedition.status === "ACTIVE" ||
    expedition.status === "OPEN" ||
    expedition.status === "PLANNING" ||
    expedition.status === "FULLY_BOOKED" ||
    expedition.status === "CANCELLED";

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="grid gap-5 lg:grid-cols-[1fr_22rem]">
      {/* Left — description + activity */}
      <div className="space-y-5">
        {/* TODO(backend/expedition): add `description` field to Expedition type.
            When ready: render this card only when description is present. */}

        {/* Activity log */}
        <motion.div variants={fadeUp}>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <ClockIcon className="size-4 text-abyss-500" />
                  Activity
                </CardTitle>
                <Button size="sm" variant="ghost" className="text-muted-foreground">
                  View all
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* TODO(backend/activity): add an activity feed endpoint to the expeditions subgraph.
                  Activity entries (crew joined, status changed, waypoint reached, etc.) should be
                  queryable as ActivityFeed($expeditionId). No backend equivalent exists yet. */}
              <p className="py-4 text-center text-sm text-muted-foreground">Activity log coming in a future update.</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Right — sticky sidebar */}
      <div className="space-y-5 lg:sticky lg:top-6 lg:self-start">
        {/* State advance panel */}
        {showStatePanel && (
          <motion.div variants={fadeUp}>
            <StateAdvancePanel status={expedition.status} />
          </motion.div>
        )}

        {/* Details */}
        <motion.div variants={fadeUp}>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <MapPinIcon className="size-4 text-abyss-500" />
                  Details
                </CardTitle>
                <Button size="sm" variant="ghost" className="text-muted-foreground">
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <dl className="space-y-2.5 text-sm">
                {expedition.kind && <DetailRow label="Kind" value={KIND_LABEL[expedition.kind]} />}
                {/* TODO(backend/expedition): add `organiser` field to Expedition type */}
                <DetailRow label="Home port" value={homePort} />
                <DetailRow label="Departure" value={departsLabel} />
                <DetailRow label="ETA" value={arrivesLabel} />
                <DetailRow label="Duration" value={duration} />
                <DetailRow label="Distance" value={fmtMiles(distanceNm)} />
                {/* TODO(backend/expedition): add `berthPrice` to Expedition type */}
              </dl>
            </CardContent>
          </Card>
        </motion.div>

        {/* Expense mini-breakdown */}
        <motion.div variants={fadeUp}>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <CreditCardIcon className="size-4 text-abyss-500" />
                  Expenses
                </CardTitle>
                <Button size="sm" variant="ghost" className="text-muted-foreground">
                  Add
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="font-heading text-2xl font-bold tabular-nums">{fmtPrice(totalExpenses, currency)}</p>
                {/* TODO(backend/expedition): add `budget` field to Expedition type to show
                    "of €X,XXX budget" subtitle. Remove this comment when wired. */}
                <p className="text-xs text-muted-foreground">total logged</p>
              </div>
              {/* Stacked category bar — shown only when categories are available from backend */}
              {expensesByCategory.length > 0 ? (
                <>
                  <div className="flex h-2 overflow-hidden rounded-full bg-muted">
                    {expensesByCategory.map((cat) => (
                      <TooltipProvider key={cat.key}>
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <div
                                className={cn("h-full", cat.color)}
                                style={{ width: `${(cat.amount / totalExpenses) * 100}%` }}
                              />
                            }
                          />
                          <TooltipContent>
                            {cat.label}: {fmtPrice(cat.amount, currency)}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    {expensesByCategory.map((cat) => (
                      <div key={cat.key} className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <span className={cn("inline-block size-2 rounded-full", cat.color)} aria-hidden />
                          {cat.label}
                        </span>
                        <span className="font-medium tabular-nums">{fmtPrice(cat.amount, currency)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : expenses.length === 0 ? (
                <p className="text-xs text-muted-foreground">No expenses logged yet.</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {expenses.length} expense{expenses.length !== 1 ? "s" : ""} logged.
                  {/* TODO(backend/expense): add `category` to show breakdown by type. */}
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Open berths CTA */}
        {open > 0 && (expedition.status === "OPEN" || expedition.status === "ACTIVE") && (
          <motion.div variants={fadeUp}>
            <div className="rounded-xl border-2 border-dashed border-border p-5 text-center">
              <p className="mb-1 text-2xl">⚓</p>
              <p className="font-heading text-[0.9375rem] font-semibold">
                {open} berth{open > 1 ? "s" : ""} available
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Open spots on {primaryVesselName} for this crossing.</p>
              <Button size="sm" className="mt-3 w-full gap-2">
                <UserPlusIcon className="size-3.5" />
                Invite crew member
              </Button>
            </div>
          </motion.div>
        )}

        {/* Completed state — no advance action needed */}
        {expedition.status === "COMPLETED" && (
          <motion.div variants={fadeUp}>
            <Card className="border-violet-200 bg-violet-50/50 dark:border-violet-800 dark:bg-violet-950/30">
              <CardContent className="space-y-2 py-5">
                <p className="text-[0.625rem] font-bold uppercase tracking-[0.07em] text-violet-600">
                  Expedition complete
                </p>
                <p className="font-heading text-sm font-semibold">Voyage complete.</p>
                <p className="text-xs text-muted-foreground">
                  This expedition is closed. Certificates and sailing miles can now be issued.
                </p>
                <Button size="sm" variant="outline" className="mt-1 gap-2">
                  <CheckCircle2Icon className="size-3.5" />
                  Issue certificates
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
